import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpEvent } from '@angular/common/http';


interface CacheItem {
  value: any;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private readonly DEFAULT_TTL = 30; // 30 segundos
  private cache = new Map<string, CacheItem>();

  // Mapa para armazenar requisições em andamento (deduplicação)
  public inFlightRequests = new Map<string, Observable<HttpEvent<any>>>();

  constructor() {}

  /**
   * Salva um valor no cache em memória.
   * TTL padrão é 30 segundos.
   */
  set(key: string, value: any, ttlSeconds?: number): void {
    const ttl = ttlSeconds ?? this.DEFAULT_TTL;
    const now = Date.now();
    const expiresAt = now + (ttl * 1000);

    this.cache.set(key, {
      value,
      expiresAt
    });
  }

  /**
   * Recupera um valor do cache.
   * Retorna null se não existir ou estiver expirado.
   */
  get(key: string): any | null {
    const item = this.cache.get(key);

    if (!item) {
      return null;
    }

    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  /**
   * Remove um item específico do cache.
   */
  remove(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Limpa todos os itens gerenciados por este serviço.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * DEBUG: Retorna todas as chaves do cache
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * DEBUG: Retorna todos os itens do cache com informações de expiração
   */
  getAll(): Array<{ key: string; value: any; expiresAt: Date; ttl: number }> {
    const now = Date.now();
    return Array.from(this.cache.entries()).map(([key, item]) => ({
      key,
      value: item.value,
      expiresAt: new Date(item.expiresAt),
      ttl: Math.max(0, Math.round((item.expiresAt - now) / 1000))
    }));
  }

  /**
   * DEBUG: Retorna estatísticas do cache
   */
  getStats(): { total: number; expired: number; active: number; inFlight: number } {
    const now = Date.now();
    let expired = 0;
    let active = 0;

    this.cache.forEach(item => {
      if (now > item.expiresAt) {
        expired++;
      } else {
        active++;
      }
    });

    return {
      total: this.cache.size,
      expired,
      active,
      inFlight: this.inFlightRequests.size
    };
  }
}
